/**
 * Technique Data Loader
 * 
 * Handles loading of technique-related data including admin techniques,
 * user techniques, and positions with proper filtering and transformation.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../../indexeddb';
import {
  TechniqueTransformer,
  UserTechniqueTransformer,
  PositionTransformer
} from '../DataTransformer';
import { BaseLoader, type ExtendedLoaderResult } from './BaseLoader';

/**
 * Technique Data Loader Implementation
 * 
 * Specializes in loading techniques, user techniques, and positions
 * with proper filtering based on configuration settings.
 */
export class TechniqueDataLoader extends BaseLoader {
  /**
   * Load all technique-related data for a user
   */
  async load(userId: string): Promise<ExtendedLoaderResult> {
    const errors: string[] = [];
    let techniquesCount = 0;
    let userTechniquesCount = 0;
    let positionsCount = 0;

    try {
      // Load admin techniques if enabled
      if (this.config.includeAdminTechniques) {
        const adminResult = await this.loadAdminTechniques();
        techniquesCount += adminResult.count;
        errors.push(...adminResult.errors);
      }

      // Load user techniques if enabled
      if (this.config.includeUserTechniques) {
        const userResult = await this.loadUserTechniques(userId);
        userTechniquesCount = userResult.count;
        errors.push(...userResult.errors);
      }

      // Load positions if enabled
      if (this.config.includeAllPositions) {
        const positionsResult = await this.loadPositions();
        positionsCount = positionsResult.count;
        errors.push(...positionsResult.errors);
      }

      return this.createExtendedLoaderResult({
        techniques: techniquesCount,
        userTechniques: userTechniquesCount,
        positions: positionsCount
      }, errors);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Technique data loading failed');
      return this.createExtendedLoaderResult({
        techniques: 0,
        userTechniques: 0,
        positions: 0
      }, [errorMessage]);
    }
  }

  /**
   * Load admin-created techniques
   */
  async loadAdminTechniques(): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('techniques')
        .select('*')
        .eq('is_admin_created', true)
        .order('name');

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlineTechniques = data.map(TechniqueTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('techniques', offlineTechniques);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Admin techniques loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load user-specific techniques
   */
  async loadUserTechniques(userId: string): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('user_techniques')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlineUserTechniques = data.map(UserTechniqueTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('user_techniques', offlineUserTechniques);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'User techniques loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load all positions (static data)
   */
  async loadPositions(): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('positions')
        .select('*')
        .order('name');

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlinePositions = data.map(PositionTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('positions', offlinePositions);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Positions loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load public techniques if enabled
   */
  async loadPublicTechniques(): Promise<{ count: number; errors: string[] }> {
    try {
      if (!this.config.includePublicTechniques) {
        return { count: 0, errors: [] };
      }

      const { data, error } = await supabase
        .from('techniques')
        .select('*')
        .eq('is_private', false)
        .order('name');

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlineTechniques = data.map(TechniqueTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('techniques', offlineTechniques);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Public techniques loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load techniques by position
   */
  async loadTechniquesByPosition(positionId: string): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('techniques')
        .select('*')
        .contains('positions', [positionId])
        .order('name');

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlineTechniques = data.map(TechniqueTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('techniques', offlineTechniques);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Position-specific techniques loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load favorite techniques for a user
   */
  async loadFavoriteTechniques(userId: string): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('user_techniques')
        .select('*')
        .eq('user_id', userId)
        .eq('is_favorite', true);

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.length > 0) {
        const offlineUserTechniques = data.map(UserTechniqueTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('user_techniques', offlineUserTechniques);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Favorite techniques loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Get technique statistics for a user
   */
  async getTechniqueStats(userId: string): Promise<{
    totalTechniques: number;
    favoriteTechniques: number;
    practicedTechniques: number;
    averageProficiency: number;
  }> {
    try {
      const userTechniques = await indexedDBService.userTechniques.getByUserId(userId);
      
      const totalTechniques = userTechniques.length;
      const favoriteTechniques = userTechniques.filter(t => t.is_favorite === 1).length;
      const practicedTechniques = userTechniques.filter(t => t.times_practiced > 0).length;
      
      // Calculate average proficiency (assuming proficiency levels: learning=1, practicing=2, competent=3, advanced=4)
      const proficiencyMap: Record<string, number> = {
        'learning': 1,
        'practicing': 2,
        'competent': 3,
        'advanced': 4
      };
      
      const totalProficiency = userTechniques.reduce((sum, t) => {
        return sum + (proficiencyMap[t.proficiency_level] || 1);
      }, 0);
      
      const averageProficiency = totalTechniques > 0 ? 
        Math.round((totalProficiency / totalTechniques) * 10) / 10 : 0;

      return {
        totalTechniques,
        favoriteTechniques,
        practicedTechniques,
        averageProficiency
      };
    } catch (error) {
      console.warn('Failed to calculate technique stats:', error);
      return {
        totalTechniques: 0,
        favoriteTechniques: 0,
        practicedTechniques: 0,
        averageProficiency: 0
      };
    }
  }
}

// Export singleton instance
export const techniqueDataLoader = new TechniqueDataLoader();
